/**
 * Splitting a refused save back out onto the workers it was refused over, and onto the field of each
 * worker it is about.
 *
 * The engagement service validates every active worker and answers with one message carrying each
 * failure, sentence by sentence, every one of them opening with the worker it is about:
 *
 *   "Worker 5: This engagement has an active billable task. Add a billable rate to this worker. If they
 *    hold none, add one to their profile. Worker 12: First Day of Work 2025-12-01 must fall within the
 *    engagement dates."
 *
 * Shown whole it is a wall of text about people the reader has to go and find. Split by worker and field
 * it can sit under the field it is about, on the card and in the worker dialog.
 */
import type { WorkerFormData as Worker } from '@/lib/schemas'
import type { TaskCoverage } from '@/lib/utils/worker-pay-shape'
import { RATE_UNIT } from '@/lib/constants/work-order-codes'

/** Opens a failure: the worker it concerns, at the start of a sentence. */
const WORKER_PREFIX = /Worker (\d+):\s*/g

/**
 * Where on a worker a failure is shown. `general` is for a failure that names no field this screen
 * recognises, which is shown at the head of the worker rather than dropped.
 */
export type WorkerErrorSlot = 'firstDayOfWork' | 'lastDayOfWork' | 'payRates' | 'general'

/** What a refusal said about one worker, by the field it is about. */
export type WorkerSaveErrors = Partial<Record<WorkerErrorSlot, string>>

/** What a refusal said, sorted by whether it named a worker. */
export interface RefusedSave {
  /** What the refusal said about each worker, keyed by their profile id. */
  byWorker: Record<number, WorkerSaveErrors>

  /**
   * Everything the refusal said that named no worker. The same message can carry both, and the part
   * that named nobody belongs to no card, so the caller has to show it or it is lost.
   */
  rest: string
}

/**
 * The field a failure is about, read off the wording the engagement service uses in
 * `Engagement.ValidateWorkersForSubmit`. A failure worded any other way lands in `general`, so a change
 * of wording there moves a message to the head of the worker rather than losing it.
 */
const slotOf = (failure: string): WorkerErrorSlot => {
  if (/^First Day of Work\b/i.test(failure)) {
    return 'firstDayOfWork'
  }
  if (/^Last Day of Work\b/i.test(failure)) {
    return 'lastDayOfWork'
  }
  if (/active (non-)?billable task|needs a Primary pay rate|is not a rate a worker can be paid at|is selected more than once/i.test(failure)) {
    return 'payRates'
  }
  return 'general'
}

/** The worker prefix is stripped off mid-sentence, so what is left starts a sentence of its own. */
const asSentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * A refusal sorted onto the workers it names, `isShownWorker` deciding which of those the reader can
 * actually open. Anything naming no worker, or naming one the page is not showing, lands in `rest`
 * whole: nothing is dropped for failing to match a shape, and the reader is never sent looking for a red
 * card over a refusal that will not put one there.
 */
export const splitRefusal = (refusal: string, isShownWorker: (id: number) => boolean): RefusedSave => {
  const byWorker: Record<number, WorkerSaveErrors> = {}

  // Each match starts a worker's part and ends where the next one begins, so the text between two
  // prefixes belongs to the first of them.
  const matches = [...refusal.matchAll(WORKER_PREFIX)]
  const unnamed: string[] = []

  const leading = refusal.slice(0, matches[0]?.index ?? refusal.length).trim()
  if (leading) {
    unnamed.push(leading)
  }

  matches.forEach((match, position) => {
    const from = (match.index ?? 0) + match[0].length
    const to = position + 1 < matches.length ? matches[position + 1].index : refusal.length
    const part = refusal.slice(from, to).trim()

    if (!part) {
      return
    }

    // A worker the page is not showing has no card to carry this, so it keeps its prefix and goes in the
    // toast whole. Marking nothing red while telling the reader to look for red is worse than the wall
    // of text.
    const workerUserProfileId = Number(match[1])
    if (!isShownWorker(workerUserProfileId)) {
      unnamed.push(`${match[0]}${part}`)
      return
    }

    // A worker can fail more than one rule, and the service reports each separately.
    const errors = (byWorker[workerUserProfileId] ??= {})
    const slot = slotOf(part)
    errors[slot] = errors[slot] ? `${errors[slot]} ${asSentence(part)}` : asSentence(part)
  })

  return { byWorker, rest: unnamed.join(' ') }
}

/** What outside the worker a refusal can depend on. */
export interface WorkerErrorContext {
  engagementStartDate?: Date | null
  engagementEndDate?: Date | null
  taskCoverage?: TaskCoverage
}

/** One refused field, with what it was refused over. */
interface StandingRefusal {
  message: string
  basis: string
}

/** The refusals held against one worker, by field. */
export type WorkerRefusals = Partial<Record<WorkerErrorSlot, StandingRefusal>>

const timeOf = (date: Date | null | undefined) => date?.getTime() ?? null

/**
 * Everything a refused field's rule reads, as one comparable value. The refusal stands for as long as
 * this is unchanged, and stops showing the moment the reader changes something the rule reads, whether
 * on the worker or elsewhere on the engagement. Whether the change fixed it is the next save's to say:
 * the worker schema checks the same rules live, so a change that did not fix it is still flagged there.
 *
 * `general` reads nothing, since which rule it came from is not known, so it stands until the next save.
 *
 * Blanks are read the way the worker dialog fills them on opening (a blank first day as the engagement
 * start, a blank unit as hourly), so the same worker reads the same on the card and in the dialog.
 */
const basisOf = (slot: WorkerErrorSlot, worker: Worker, context: WorkerErrorContext): string => {
  const rates = (worker.payRates ?? []).map(rate => [
    rate.rateTypeCode ?? null,
    rate.billable !== false,
    rate.payRate ?? null,
    rate.payRateUnitCode || RATE_UNIT.HOUR,
  ])

  switch (slot) {
    case 'firstDayOfWork':
      return JSON.stringify([
        timeOf(worker.firstDayOfWork ?? context.engagementStartDate),
        timeOf(context.engagementStartDate),
        timeOf(context.engagementEndDate),
      ])
    case 'lastDayOfWork':
      return JSON.stringify([
        timeOf(worker.lastDayOfWork),
        timeOf(worker.firstDayOfWork ?? context.engagementStartDate),
        timeOf(context.engagementStartDate),
        timeOf(context.engagementEndDate),
      ])
    case 'payRates':
      return JSON.stringify([
        rates,
        context.taskCoverage?.needsBillable ?? null,
        context.taskCoverage?.needsNonBillable ?? null,
      ])
    case 'general':
      return ''
  }
}

/** A worker's refused fields, each held against what it was refused over. */
export const recordRefusals = (errors: WorkerSaveErrors, worker: Worker, context: WorkerErrorContext): WorkerRefusals => {
  const refusals: WorkerRefusals = {}

  for (const [slot, message] of Object.entries(errors) as [WorkerErrorSlot, string][]) {
    refusals[slot] = { message, basis: basisOf(slot, worker, context) }
  }

  return refusals
}

/** The refused fields that still stand for `worker` as it is now. */
export const standingRefusals = (
  refusals: WorkerRefusals | undefined,
  worker: Worker,
  context: WorkerErrorContext
): WorkerSaveErrors => {
  const standing: WorkerSaveErrors = {}

  for (const [slot, refusal] of Object.entries(refusals ?? {}) as [WorkerErrorSlot, StandingRefusal][]) {
    if (refusal.basis === basisOf(slot, worker, context)) {
      standing[slot] = refusal.message
    }
  }

  return standing
}
